import i18next from "i18next";
import { inject } from "mobx-react";
import React, { useEffect } from "react";
import { Translation } from "react-i18next";
import type { Store } from "../../../../client/stores";
import type { BlockFragment, OnAirShowFragment } from "../../generated/webapi.g";
import { placeholderImage } from "../../lib/image";
import { formatImage } from "../../lib/image-asset";
import { reverseRoute } from "../../lib/route";
import { getOnAirNow } from "../../services/OnAir";
import { DateTimeDisplay, FauxButton } from "../../ui";
import { ButtonKind } from "../../ui";
import { OnairScheduleThumb } from "../onair/OnairScheduleThumb.component";
import "./OnairTile.style.scss";
import { ScheduleLinkWrapper } from "./ScheduleLinkWrapper.component";

interface OnairTileProps {
    current?: OnAirShowFragment | null;
    next?: OnAirShowFragment | null;
    cacheKey?: string;
    block: BlockFragment;
    store?: Store;
}

const startOnairRefresh = (store: Store, cacheKey: string, interval: number): ReturnType<typeof setInterval> | null => {
    if (!store || !cacheKey || !interval) {
        return null;
    }

    if (typeof window !== "undefined") {
        return setInterval(async () => {
            const data = await getOnAirNow({
                slug: store.site.index.slug,
                currentDate: store.env.TIME_OVERRIDE ? parseFloat(store.env.TIME_OVERRIDE) : undefined,
            });
            if (data) {
                store.cache.set(cacheKey, { data });
            }
        }, interval);
    }

    return null;
};

const stopOnairRefresh = (intervalId: ReturnType<typeof setInterval> | null) => {
    if (typeof window !== "undefined" && intervalId) {
        clearInterval(intervalId);
    }
};

export const OnairTile = inject("store")(({ store, current, next, cacheKey, block }: OnairTileProps) => {
    if (!store || (!current && !next)) {
        return null;
    }

    useEffect(() => {
        store.storeBlock({ ...block });
        const timestamp = store.env.ONAIR_ROTATOR_REFRESH ? parseInt(store.env.ONAIR_ROTATOR_REFRESH, 10) : 30000;
        if (cacheKey) {
            const timer = startOnairRefresh(store, cacheKey, timestamp);
            return () => stopOnairRefresh(timer);
        }
    }, [store, cacheKey, block]);

    const { site } = store;

    return (
        <div className="component-onair-tile themed-block">
            <nav className="tile-container">
                <Translation>
                    {t => (
                        <OnAirTileSchedule
                            schedule={current}
                            caption={t("onair")}
                            context="current"
                            stationLogo={site.sections.design?.station_logo?.id}
                        />
                    )}
                </Translation>
                <Translation>
                    {t => (
                        <OnAirTileSchedule
                            schedule={next}
                            caption={t("onairnext")}
                            context="upcoming"
                            stationLogo={site.sections.design?.station_logo?.id}
                        />
                    )}
                </Translation>
                <section className="full-schedule">
                    <FauxButton
                        to={reverseRoute(site, "schedule") || "/schedule/"}
                        context="full_schedule"
                        kind={ButtonKind.SECONDARY}
                        size="small"
                    >
                        {i18next.t("full_schedule")}
                    </FauxButton>
                </section>
            </nav>
        </div>
    );
});

interface OnAirTileScheduleProps {
    schedule?: OnAirShowFragment | null;
    caption: string;
    context: string;
    stationLogo: string | null | undefined;
    store?: Store;
}

const OnAirTileSchedule = inject("store")(
    ({ schedule, caption, context, stationLogo, store }: OnAirTileScheduleProps) => {
        if (!store) {
            return null;
        }
        const { env } = store;
        let imgUrl = formatImage(stationLogo || "", env) || placeholderImage;
        let { name } = schedule || { name: store.site.sections.general?.name };
        let href: string | undefined;
        if (schedule?.showSite?.configByLookup) {
            name = schedule.showSite?.configByLookup?.sections?.general?.name || "";
            const image = schedule.showSite?.configByLookup?.sections?.general?.thumbnail_image?.asset?.href;
            if (schedule.showSite?.slug) {
                href = `/featured/${schedule.showSite?.slug}/`;
            }
            if (image) {
                imgUrl = image;
            }
        }

        const timezone = store.site.index.timeZone || "";

        return (
            <figure>
                <section className="thumb-container">
                    <ScheduleLinkWrapper href={href} context={context}>
                        <OnairScheduleThumb alt={caption} imgUrl={imgUrl} min={40} max={80} />
                    </ScheduleLinkWrapper>
                </section>
                <figcaption>
                    <ScheduleLinkWrapper href={href} context={context}>
                        <span className="eyebrow-like">{caption}</span>
                        {schedule ? (
                            <DateTimeDisplay timestamp={schedule.startMs} zone_id={timezone} format="h:mm a" />
                        ) : null}
                        <span className="show-title">{name}</span>
                    </ScheduleLinkWrapper>
                </figcaption>
            </figure>
        );
    },
);
